'use client';

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mail, CheckCircle, AlertCircle, MapPin, Calendar, Clock, ArrowRight, X, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import ImagesDev from '../api/images';
import { EventsHighlights } from '../components/EventsHighlights';
import { StackedCarousel } from '../components/react-card-stack-carousel-main/src/StackedCarousel';
import { Toast } from '../components/Toast';

interface FormData {
    first_name: string;
    last_name: string;
    email_address: string;
    volunteer_role: string;
    isEmailAvailable: boolean;
}

interface FormErrors {
    [key: string]: string;
}


function BST_Testimonies() {
    const [activeIndex, setActiveIndex] = useState(0);
    const [modalImage, setModalImage] = useState<string | null>(null);


    const handleNext = () => {
        if (activeIndex < ImagesDev.bstTestimonies.length - 1) {
            setActiveIndex(activeIndex + 1);
        }
    };

    const handleBack = () => {
        if (activeIndex > 0) {
            setActiveIndex(activeIndex - 1);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative h-full w-full flex items-center"
        >
            <StackedCarousel
                pagination
                loop
                height={530}
                startIndex={0}
                className="h-80"
            >
                {ImagesDev.bstTestimonies.map((item) => (
                    <div
                        key={item.img}
                        onClick={() => setModalImage(item.img)}
                        className="overflow-hidden px-0 h-[460px] cursor-pointer"
                    >
                        <img
                            src={item.img}
                            alt="Bible Study"
                            className="w-full h-full object-cover object-center shadow-md rounded-2xl "
                        />
                    </div>
                ))}
            </StackedCarousel>

            <AnimatePresence>
                {modalImage && (
                    <motion.div
                        className="fixed inset-0 z-50 bg-black/90 backdrop-blur flex items-center justify-center"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setModalImage(null)}
                    >
                        <div className="relative max-w-3xl w-full px-4">
                            <img src={modalImage} alt="Zoomed Testimony" className="rounded-xl w-full h-auto max-h-[80vh] object-contain" />
                            <button
                                className="absolute top-4 right-4 bg-white/10 p-2 rounded-full hover:bg-white/20"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setModalImage(null);
                                }}
                            >
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default function BibleStudyRegistration() {
    const [formData, setFormData] = useState<FormData>({
        first_name: '',
        last_name: '',
        email_address: '',
        volunteer_role: '',
        isEmailAvailable: false,
    });

    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [emailAvailable, setEmailAvailable] = useState<boolean | null>(null);

    const validateEmail = (email: string): boolean => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    // const checkEmailAvailability = async (email: string) => {
    //     if (!validateEmail(email)) {
    //         setEmailAvailable(false);
    //         return;
    //     }

    //     try {
    //         const response = await fetch('https://tsoministries.com/wp-json/wp/v2/bstm', {
    //             method: 'GET',
    //         });

    //         if (response.ok) {
    //             const registrations = await response.json();
    //             const emailExists = registrations.some(
    //                 (reg: any) => reg.email === email
    //             );
    //             setEmailAvailable(!emailExists);
    //         } else {
    //             setEmailAvailable(true);
    //         }
    //     } catch (error) {
    //         console.error('Error checking email:', error);
    //         setEmailAvailable(null);
    //     }
    // };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleChange = (e: any) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
        if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
        if (!formData.email_address.trim()) {
            newErrors.email_address = 'Email is required';
        } else if (!validateEmail(formData.email_address)) {
            newErrors.email_address = 'Invalid email format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        if (!formData.isEmailAvailable) {
            Toast.error('Email is already in use. Please use another email.');
            return;
        }

        setIsSubmitting(true);

        try {
            const response = await fetch('https://tsoministries.com/wp-json/custom-form/v1/bstm', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            });

            if (response.ok) {
                Toast.success("Registration successful!");
                setIsSuccess(true);
                setFormData({ first_name: '', last_name: '', email_address: '', volunteer_role: '', isEmailAvailable: false });

            } else {
                setErrors({ submit: 'Failed to register. Please try again.' });
            }
        } catch (error:any) {
            Toast.error(error?.message || 'Failed to submit form');
            setErrors({ submit: 'An error occurred. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-black text-white">
            {/* Hero Section */}
            <div className="relative min-h-[90vh] flex items-center overflow-hidden py-20">
                {/* Background Gradient */}

                <motion.div
                    initial={{ scale: 1.2 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 1.5 }}
                    className="absolute inset-0"
                >
                    <img
                        src={ImagesDev.TSO_BG}
                        alt="Bible Study"
                        className="w-full h-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80" />
                </motion.div>
                <div className="absolute inset-0 bg-gradient-to-br from-amber-900/30 via-orange-900/20 to-black" />

                <div className="relative container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: -30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    // className="max-w-4xl"
                    >
                        <div className="inline-flex items-center space-x-2 bg-white/10 rounded-full px-4 py-2 mb-8 backdrop-blur-sm">
                            <span className="w-2 h-2 bg-amber-400 rounded-full animate-pulse" />
                            <span className="text-sm text-gray-300">April 25, 2026</span>
                        </div>

                        <h1 className="text-6xl md:text-7xl font-bold leading-tight mb-6">
                            Bible Study
                            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-amber-400 via-orange-400 to-amber-400">
                                Masterclass
                            </span>
                        </h1>

                        <div className="space-y-6 mb-8">
                            <p className="text-xl md:text-2xl text-gray-200 leading-relaxed max-w-2xl">
                                The Bible Study Masterclass, a program of TSO Ministries, is designed to help you read the Bible with Jesus at the center of every passage. Rather than seeing Scripture as disconnected stories or rules, this program reveals how each book, chapter, and verse points to Christ.
                            </p>
                        </div>

                        <div className='flex flex-col lg:flex-row gap-4 md:gap-8 mb-6'>
                            <div className='bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-amber-400/50 transition-colors'>
                                <p className="text-lg text-gray-300 leading-relaxed max-w-2xl">
                                    You&apos;ll gain practical tools and clear guidance to uncover the presence and purpose of Jesus throughout the Bible, transforming your reading into a meaningful, gospel-centered journey that deepens both understanding and faith.
                                </p>
                            </div>

                            <div className='bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:border-amber-400/50 transition-colors'>
                                <p className="text-lg text-gray-300 leading-relaxed max-w-2xl">
                                    When we see Christ clearly, we begin to see ourselves rightly, because Christ is our identity. His life, love, and redemptive work define who we truly are. Every story, teaching, and passage becomes a mirror reflecting our place in Him, helping us grow in confidence, purpose, and spiritual depth.
                                </p>
                            </div>
                        </div>

                        <div>
                            <p className="text-lg text-gray-400 italic font-serif">
                                The Bible Study Masterclass is more than a program, it&apos;s an invitation to experience Scripture in a life-changing way.
                            </p>
                        </div>

                        <div className="flex items-center gap-4 pt-6 border-t border-white/10">
                            <span className="text-amber-400 font-semibold">... till every eye sees Jesus</span>
                        </div>
                    </motion.div>
                </div>

                {/* Scroll Indicator */}
                <motion.div
                    animate={{ y: [0, 10, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
                >
                    <div className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2">
                        <div className="w-1 h-2 bg-white/60 rounded-full" />
                    </div>
                </motion.div>
            </div>

            {/* Event Details Section */}
            <div className="container mx-auto px-4 py-16">
                <div className="grid grid-cols-3 gap-2 md:gap-6 mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.1 }}
                        className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 md:p-8 border border-white/20 hover:border-amber-400/50 transition-colors"
                    >
                        <Calendar className="w-4 h-4 md:w-8 md:h-8 text-amber-400 mb-4" />
                        <h3 className="text-sm md:text-lg font-bold mb-2">Date</h3>
                        <p className="text-[8px] md:text-sm text-gray-300">25th April 2026</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                        className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 md:p-8 border border-white/20 hover:border-amber-400/50 transition-colors"
                    >
                        <Clock className="w-4 h-4 md:w-8 md:h-8 text-amber-400 mb-4" />
                        <h3 className="text-sm md:text-lg  font-bold mb-2">Time</h3>
                        <p className="text-[8px] md:text-sm text-gray-300">10:00 AM</p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                        className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 md:p-8 border border-white/20 hover:border-amber-400/50 transition-colors"
                    >
                        <MapPin className="w-4 h-4 md:w-8 md:h-8 text-amber-400 mb-4" />
                        <h3 className="text-sm md:text-lg  font-bold mb-2">Venue</h3>
                        <p className="text-[8px] md:text-sm text-gray-300">50, Hakeem Balogun Street, CBD, Alausa, Ikeja</p>
                    </motion.div>
                </div>

                {/* Main Content - Registration & Testimonies */}

                <div className="grid grid-cols-1 md:grid-cols-[30%_70%] gap-8 mb-10 mt-0 md:px-0 md:pr-12">

                    {/* Testimonies Section */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.2 }}
                    >
                        <BST_Testimonies />
                    </motion.div>

                    {/* Form Section */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 }}
                    >
                        <AnimatePresence mode="wait">
                            {isSuccess ? (
                                <motion.div
                                    key="success"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    className="bg-gradient-to-br from-green-600/20 to-green-900/20 rounded-2xl p-12 border border-green-500/20"
                                >
                                    <div className="text-center">
                                        <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                                        <h3 className="text-2xl font-bold mb-2">Registration Successful!</h3>
                                        <p className="text-gray-300">
                                            Thank you for registering for the Bible Study Masterclass. A confirmation email has been sent to <span className="text-amber-400 font-semibold">{formData.email_address}</span>.
                                        </p>
                                        <p className="text-gray-400 text-sm mt-4">
                                            We look forward to seeing you on April 25, 2026.
                                        </p>
                                    </div>
                                </motion.div>
                            ) : (
                                <motion.form
                                    key="form"
                                    initial={{ opacity: 0, scale: 0.95 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    onSubmit={handleSubmit}
                                    className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 space-y-6 sticky top-20"
                                >
                                    <h3 className="text-2xl font-bold mb-6">Register Now</h3>

                                    <div className='flex flex-row md:flow-col gap-5'>
                                        {/* First Name */}
                                        <div className='w-full'>
                                            <label className="block text-sm font-medium mb-2 text-gray-200">First Name</label>
                                            <input
                                                type="text"
                                                name="first_name"
                                                value={formData.first_name}
                                                onChange={handleInputChange}
                                                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 transition-colors"
                                                placeholder="John"
                                                disabled={isSubmitting}
                                            />
                                            {errors.first_name && (
                                                <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" /> {errors.first_name}
                                                </p>
                                            )}
                                        </div>

                                        {/* Last Name */}
                                        <div className='w-full'>
                                            <label className="block text-sm font-medium mb-2 text-gray-200">Last Name</label>
                                            <input
                                                type="text"
                                                name="last_name"
                                                value={formData.last_name}
                                                onChange={handleInputChange}
                                                className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 transition-colors"
                                                placeholder="Doe"
                                                disabled={isSubmitting}
                                            />
                                            {errors.last_name && (
                                                <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                                                    <AlertCircle className="w-4 h-4" /> {errors.last_name}
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Email */}
                                    {/* Email Address */}
                                    <BBBEmailInput
                                        value={formData.email_address}
                                        onChange={(e: any) => handleChange(e)}
                                        setForm={setFormData}
                                    />
                                    {/* <div>
                    <label className="block text-sm font-medium mb-2 text-gray-200">Email Address</label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email_address"
                        value={formData.email_address}
                        onChange={handleInputChange}
                        className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 transition-colors"
                        placeholder="john@example.com"
                        disabled={isSubmitting}
                      />
                      {emailAvailable === true && (
                        <CheckCircle className="absolute right-3 top-3 w-5 h-5 text-green-400" />
                      )}
                      {emailAvailable === false && (
                        <AlertCircle className="absolute right-3 top-3 w-5 h-5 text-red-400" />
                      )}
                    </div>
                    {errors.email_address && (
                      <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" /> {errors.email_address}
                      </p>
                    )}
                  </div> */}

                                    {/* Volunteer Role */}
                                    <div>
                                        <label className="block text-sm font-medium mb-2 text-gray-200">
                                            Will you like to volunteer?
                                        </label>
                                        <select
                                            name="volunteer_role"
                                            value={formData.volunteer_role}
                                            onChange={handleInputChange}
                                            className="w-full bg-white/10 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-400/50 transition-colors"
                                            disabled={isSubmitting}
                                        >
                                            <option value="" className="bg-gray-900">Select a role (Optional)</option>
                                            <option value="Registration desk" className="bg-gray-900">Registration desk</option>
                                            <option value="Ushers/Protocol" className="bg-gray-900">Ushers/Protocol</option>
                                            <option value="Media/content" className="bg-gray-900">Media/content</option>
                                        </select>
                                    </div>

                                    {errors.submit && (
                                        <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
                                            <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                                            <p className="text-red-300 text-sm">{errors.submit}</p>
                                        </div>
                                    )}

                                    {/* Submit Button */}
                                    <motion.button
                                        type="submit"
                                        whileHover={{ scale: 1.02 }}
                                        whileTap={{ scale: 0.98 }}
                                        disabled={isSubmitting || emailAvailable === false}
                                        className="w-full py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <motion.div
                                                    animate={{ rotate: 360 }}
                                                    transition={{ duration: 1, repeat: Infinity }}
                                                    className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                                                />
                                                Registering...
                                            </>
                                        ) : (
                                            <>
                                                Register <ArrowRight className="w-4 h-4" />
                                            </>
                                        )}
                                    </motion.button>
                                </motion.form>
                            )}
                        </AnimatePresence>
                    </motion.div>
                </div>

                {/* Event Highlights */}
                <div className="container mx-auto px-4 pb-2">
                    <EventsHighlights parent="bible_study" noHeader={false} />
                </div>

            </div>
        </div>
    );
}



// ---------------------------
// BBBEmailInput component
// ---------------------------
type BBBEmailInput = {
    value: string;
    onChange: any;
    setForm: any
}
const BBBEmailInput = ({ value, onChange, setForm }: BBBEmailInput) => {
    const [email, setEmail] = useState(value || "");
    const [isValid, setIsValid] = useState(true);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [showEmailWarning, setShowEmailWarning] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!email) {
            setIsValid(true);
            setIsAvailable(null);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const isEmailValid = emailRegex.test(email);
        setIsValid(isEmailValid);
        setShowEmailWarning(true);

        if (!isEmailValid) {
            setIsAvailable(null);
            return;
        }

        const delay = setTimeout(async () => {
            setLoading(true);
            try {
                const available = await checkEmailAvailability(email);
                setIsAvailable(available);
            } catch (error) {
                console.error("Error checking email availability:", error);
                setIsAvailable(null); // Force null on any error
            } finally {
                setLoading(false);
            }
        }, 500);

        return () => clearTimeout(delay);
    }, [email]);

    // Always update parent form with current email availability
    useEffect(() => {
        if (!email) {
            setForm((prev: any) => ({ ...prev, isEmailAvailable: null }));
            return;
        }

        if (isValid) {
            setForm((prev: any) => ({ ...prev, isEmailAvailable: isAvailable }));
        } else {
            setForm((prev: any) => ({ ...prev, isEmailAvailable: false }));
        }
    }, [email, isValid, isAvailable, setForm]);

    const handleChange = (e: any) => {
        setEmail(e.target.value);
        onChange({ target: { name: "email_address", value: e.target.value } });
    };

    return (
        <div className="mb-6">
            <label className="block text-sm font-medium mb-2">Email Address</label>
            <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                    type="email"
                    name="email_address"
                    value={email}
                    onChange={handleChange}
                    className="w-full pl-10 pr-4 py-3 bg-white/5 rounded-lg border border-white/10 focus:outline-none focus:ring-2 focus:ring-pink-400 focus:border-transparent transition-all duration-300"
                    required
                />
                {loading ? (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                        Checking...
                    </div>
                ) : showEmailWarning && isValid && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {isAvailable === true ? (
                            <CheckCircle className="text-green-500 w-5 h-5" />
                        ) : isAvailable === false ? (
                            <XCircle className="text-red-500 w-5 h-5" />
                        ) : email.length >= 1 && (
                            <AlertCircle className="text-yellow-500 w-5 h-5" />
                        )}
                    </div>
                )}
            </div>

            {showEmailWarning && !isValid && email.length > 0 && (
                <p className="text-red-500 text-sm mt-1">Invalid email format.</p>
            )}

            {showEmailWarning && isValid && isAvailable === false && (
                <p className="text-red-500 text-sm mt-1">
                    This email is already in use. You are registered already.
                </p>
            )}

            {email.length >= 1 && showEmailWarning && isValid && isAvailable === null && !loading && (
                <p className="text-yellow-500 text-sm mt-1">
                    Unable to verify email. Please check your internet connection.
                </p>
            )}
        </div>
    );
};



// -----------------------------------------
// checkEmailAvailability (utility function)
// -----------------------------------------
async function checkEmailAvailability(email: string) {
    const endpoint = 'https://tsoministries.com/wp-json/wp/v2/bstm';
    let page = 1;
    let emailExists = false;

    try {
        while (true) {
            const response = await fetch(`${endpoint}?per_page=100&page=${page}&_fields=id,email_address`);

            if (!response.ok) {
                // If 400 and page > 1, assume we've reached the end
                if (response.status === 400 && page > 1) {
                    break;
                }
                throw new Error(`Error fetching page ${page}`);
            }

            const data = await response.json();

            if (!Array.isArray(data) || data.length === 0) {
                break;
            }

            for (const item of data) {
                if (item.email_address && item.email_address.toLowerCase() === email.toLowerCase()) {
                    emailExists = true;
                    break;
                }
            }

            if (emailExists || data.length < 100) {
                break;
            }

            page++;
        }

        return !emailExists; // true = available
    } catch (error) {
        console.error('Failed to check email v2:', error);
        return null;
    }
}